import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import useStyles, { CustomTooltip } from "./dependents-collapse.style";
import accountChild from "@iconify/icons-mdi/account-child";
import informationOutline from "@iconify/icons-mdi/information-outline";
import { Icon } from "@iconify/react";
import ApplicationCollapseHeader from "my-tax/components/application-collapse-header";
import MyTaxChildTypeInfo from "my-tax/components/child-type-info";
import MyTaxDependentQualityInfo from "my-tax/components/dependent-quality-info";
import MyTaxEligibilityQualityInfo from "my-tax/components/eligibility-quality-info";
import { NOT_PROVIDED } from "my-tax/constants";
import { OcDatePicker, OcErrorBoundary } from "oc/components";
import { useRecoilState } from "recoil";
import { ocCollapseState } from "oc/states";
import { getDateFormatByLocale } from "oc/utils";

interface MyTaxDependentsCollapseProps {
  index: number;
  data: any;
  id: string;
  disabled?: boolean;
  onChange: (props: any) => void;
  isModifiedApplication: boolean;
  editable: boolean;
  isMobile: boolean;
  variant?: "default" | "combined";
}

export const MyTaxDependentsCollapse = ({
  index,
  data,
  id,
  disabled = false,
  onChange,
  isModifiedApplication,
  editable,
  isMobile,
  variant = "default",
}: MyTaxDependentsCollapseProps) => {
  const { t, i18n } = useTranslation();
  let label = t("MY_TAX_PLEASE_SELECT");

  const [expanded, setExpanded] = useRecoilState(
    ocCollapseState(`dependentsFilterCollapseExpanded${index}`)
  );

  const classes = useStyles(expanded);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const handleChange = (props: any) => {
    onChange(props);
  };

  return (
    <OcErrorBoundary>
      <Box>
        <ApplicationCollapseHeader
          handleExpandClick={handleExpandClick}
          expanded={expanded}
          icon={accountChild}
          dependentName={data?.name}
          dependentTaxNumber={data?.taxNumber ? data?.taxNumber : NOT_PROVIDED}
          taxNumberLabel="Adóazonosító jele"
          isMobile={isMobile}
        />
        <Collapse in={expanded} timeout="auto">
          <Box className={classes.backgroundColor} padding="0px 16px">
            <Box className={classes.topBorder} />
            <Box display="flex" flexDirection="row" alignItems="center">
              <Typography
                variant="body1"
                style={{
                  padding: "16px 0px",
                  marginRight: "8px",
                }}
              >
                Eltartotti minőség
              </Typography>
              <CustomTooltip
                title={<MyTaxDependentQualityInfo />}
                placement="right"
              >
                <IconButton aria-label="Info">
                  <Icon
                    icon={informationOutline}
                    width="20px"
                    color="#919699"
                  />
                </IconButton>
              </CustomTooltip>
            </Box>
            <FormControl
              id={`dependents-quality-${index}`}
              variant="outlined"
              disabled={disabled || isModifiedApplication}
              style={{
                height: "56px",
                width: isMobile ? "100%" : "467px",
              }}
            >
              <InputLabel htmlFor={`${id}-dependent-type`}>{label}</InputLabel>
              <Select
                value={data?.dependentType || ""}
                label={label}
                inputProps={{
                  id: `${id}-dependent-type`,
                  name: `${id}-dependent-type`,
                }}
                onChange={(e) => {
                  handleChange({
                    ...data,
                    dependentType: e.target.value,
                  });
                }}
                disabled={!editable}
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                }}
              >
                <MenuItem value="">-- {t("OC_NOT_SELECTED")} --</MenuItem>
                <MenuItem value="1">1. Kedvezményezett eltartott</MenuItem>
                <MenuItem value="2">2. Eltartott</MenuItem>
                <MenuItem value="3">3. Felváltva gondozott gyermek</MenuItem>
                <MenuItem value="4">
                  4. Tartósan beteg, illetve súlyosan fogyatékos személy
                  (gyermek)
                </MenuItem>
                <MenuItem value="5">
                  5. Felváltva gondozott tartósan beteg, illetve súlyosan
                  fogyatékos személy (gyermek)
                </MenuItem>
                <MenuItem value="0">0. Kedvezménybe nem számító</MenuItem>
              </Select>
            </FormControl>
            <Box height="18px" />
            <Box display="flex" flexDirection="row" alignItems="center">
              <Typography
                variant="body1"
                style={{
                  padding: "16px 0px",
                  marginRight: "8px",
                }}
              >
                Jogosultság jogcíme
              </Typography>
              <CustomTooltip
                title={<MyTaxEligibilityQualityInfo />}
                placement="right"
              >
                <IconButton aria-label="Info">
                  <Icon
                    icon={informationOutline}
                    width="20px"
                    color="#919699"
                  />
                </IconButton>
              </CustomTooltip>
            </Box>
            <FormControl
              id={`eligibility-title-${index}`}
              variant="outlined"
              disabled={disabled}
              style={{
                height: "56px",
                width: isMobile ? "100%" : "467px",
              }}
            >
              <InputLabel htmlFor={`${id}-entitlement-title`}>{label}</InputLabel>
              <Select
                value={data?.entitlementTitle || ""}
                label={label}
                inputProps={{
                  id: `${id}-entitlement-title`,
                  name: `${id}-entitlement-title`,
                }}
                onChange={(e) => {
                  handleChange({
                    ...data,
                    entitlementTitle: e.target.value,
                  });
                }}
                disabled={!editable}
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                }}
              >
                <MenuItem value="">-- {t("OC_NOT_SELECTED")} --</MenuItem>
                <MenuItem value="a">
                  a. Gyermek után családi pótlékra jogosult vagy ilyen
                  jogosulttal közös háztartásban élő házastárs
                </MenuItem>
                <MenuItem value="b">
                  b. Várandós nő vagy várandós nő közös háztartásban élő
                  házastársa.
                </MenuItem>
                <MenuItem value="c">
                  c. Családi pótlékra saját jogán jogosult, vagy ilyen
                  jogosulttal közös háztartásban élő hozzátartozó (ideértve a
                  gyermek szüleinek hozzátartozóit is)
                </MenuItem>
                <MenuItem value="d">
                  d. Rokkantsági járadékban részesülő vagy ilyen személlyel
                  közös háztartásban élő hozzátartozó
                </MenuItem>
              </Select>
            </FormControl>
            <Box height="16px" />
            {variant === "combined" && (
              <>
                <Box display="flex" flexDirection="row" alignItems="center">
                  <Typography
                    variant="body1"
                    style={{
                      padding: "16px 0px",
                      marginRight: "8px",
                    }}
                  >
                    Gyermek típusa
                  </Typography>
                  <CustomTooltip
                    title={<MyTaxChildTypeInfo />}
                    placement="right"
                  >
                    <IconButton aria-label="Info">
                      <Icon
                        icon={informationOutline}
                        width="20px"
                        color="#919699"
                      />
                    </IconButton>
                  </CustomTooltip>
                </Box>
                <FormControl
                  id={`child-type-${index}`}
                  variant="outlined"
                  disabled={disabled}
                  style={{
                    height: "56px",
                    width: isMobile ? "100%" : "467px",
                  }}
                >
                  <InputLabel htmlFor={`child-type-${id}`}>{label}</InputLabel>
                  <Select
                    value={data?.childType || ""}
                    label={label}
                    inputProps={{
                      id: `child-type-${id}`,
                    }}
                    onChange={(e) => {
                      handleChange({
                        ...data,
                        childType: e.target.value,
                      });
                    }}
                    disabled={!editable}
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                    }}
                  >
                    <MenuItem value="">-- {t("OC_NOT_SELECTED")} --</MenuItem>
                    <MenuItem value="V">V. Vérszerinti gyerek</MenuItem>
                    <MenuItem value="O">O. Örökbefogadott gyerek</MenuItem>
                  </Select>
                </FormControl>
                <Box height="16px" />
              </>
            )}
            <Collapse in={isModifiedApplication}>
              <Box>
                <Typography
                  variant="body1"
                  style={{
                    padding: "16px 0px",
                    marginRight: "8px",
                  }}
                >
                  A változás időpontja
                </Typography>
                <Box
                  width={isMobile ? "100%" : "467px"}
                  id={`dependents-collapse-date-picker-${index}`}
                >
                  <OcDatePicker
                    id={`dependents-collapse-date-picker-input-${index}`}
                    value={
                      data?.changeDate &&
                        !Number.isNaN(new Date(data.changeDate).getTime())
                        ? new Date(data.changeDate)
                        : null
                    }
                    disabled={!editable || !isModifiedApplication}
                    onChange={(e) => {
                      handleChange({
                        ...data,
                        changeDate: e,
                      });
                    }}
                    format={getDateFormatByLocale(i18n.language)}
                  />
                </Box>
                <Box height="16px" />
              </Box>
            </Collapse>
          </Box>
        </Collapse>
      </Box>
    </OcErrorBoundary>
  );
};

export default MyTaxDependentsCollapse;
